import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import * as XLSX from "xlsx"

import { FiArrowLeft } from "react-icons/fi"
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa"
import { RiDeleteBinLine } from "react-icons/ri"
import Button from "react-bootstrap/Button"
import "./index.scss"

import { ConfirmDialog, Loading } from "components"
import { integrationSheets, message } from "state_management"
import useT from "utils/hooks/useT"
import {
  applyCellText,
  cellText,
  displayOrder,
  exportRows,
  isCellValid,
  nextSort,
  orderHeaders,
  parseClipboard,
} from "./gridUtils"

const BACK_URL = "/setup/integration"
const BACK_STATE = { state: { scrollToAutomatic: true } }
const GO_BACK_CONFIRM_MS = 10000
const EDITABLE = "[contenteditable]"

// Client-side row identity. Never sent to the server.
let lastRowKey = 0
const newRowKey = () => `row-${++lastRowKey}`
const toGridRows = (rows = []) => rows.map((data) => ({ key: newRowKey(), data }))

// Loads the sheet, then hands it to the grid. The grid owns the edit state and is keyed by the
// sheet id, so it always starts from the stored sheet.
export default function DataGridLayout() {
  const { t: tc } = useT("common")
  const dispatch = useDispatch()
  const { sheetId } = useParams()

  const isLoading = useSelector((state) => state.integrationSheets.isLoadingConversionSheet)
  const sheet = useSelector((state) => state.integrationSheets.conversionSheets[sheetId])
  const limsSheets = useSelector((state) => state.integrationSheets.limsSheets)
  const sapSheets = useSelector((state) => state.integrationSheets.sapSheets)

  const sheetType = sheetTypeOf(sheetId, limsSheets, sapSheets)

  useEffect(() => {
    if (!sheetType) {
      // The sheet type is only known from the upload-config lists.
      dispatch(integrationSheets.fetchLimsSheets())
      dispatch(integrationSheets.fetchSapSheets())
    } else if (sheet === undefined) {
      const fetchSheet =
        sheetType === "Sample" ? integrationSheets.fetchLimsConversionSheet : integrationSheets.fetchSapConversionSheet
      dispatch(fetchSheet(sheetId))
    }
  }, [dispatch, sheet, sheetId, sheetType])

  if (isLoading || !sheet || !sheetType) {
    return <Loading title={tc("loadingSheet")} />
  }
  return <ConversionSheetGrid key={sheetId} sheet={sheet} sheetId={sheetId} sheetType={sheetType} />
}

function sheetTypeOf(sheetId, limsSheets, sapSheets) {
  if (limsSheets?.some((s) => s.id === sheetId)) return "Sample"
  if (sapSheets?.some((s) => s.id === sheetId)) return "Project"
  return ""
}

function initGrid(sheet) {
  const rows = toGridRows(sheet.rows)
  return { pristine: rows, rows, touched: new Map(), showErrors: false, sort: null }
}

const markTouched = (touched, rowKey, colKeys) => {
  const next = new Map(touched)
  next.set(rowKey, new Set([...(touched.get(rowKey) || []), ...colKeys]))
  return next
}

// Only the edited rows get new objects, so unchanged rows keep their identity and skip rendering.
function gridReducer(state, action) {
  switch (action.type) {
    case "edit": {
      const { rowKey, colKey, text } = action
      const row = state.rows.find((r) => r.key === rowKey)
      if (!row) return state
      const data = applyCellText(row.data, colKey, text)
      const touched = markTouched(state.touched, rowKey, [colKey])
      if (data[colKey] === row.data[colKey] && (colKey in data) === (colKey in row.data)) {
        return { ...state, touched }
      }
      return { ...state, touched, rows: state.rows.map((r) => (r === row ? { ...r, data } : r)) }
    }
    case "paste": {
      // Pasted rows follow the on-screen order; rows beyond the end become new rows.
      const { rowKey, colKey, cells, headers, newKeys } = action
      const order = displayOrder(state.rows, state.sort)
      const start = order.findIndex((r) => r.key === rowKey)
      const firstCol = headers.findIndex((h) => h.key === colKey)
      if (start === -1 || firstCol === -1) return state
      const updates = new Map()
      const added = []
      let touched = state.touched
      cells.forEach((line, i) => {
        const target = order[start + i] || { key: newKeys[added.length], data: {}, isNew: true }
        if (!order[start + i]) added.push(target)
        let data = updates.get(target.key)?.data || target.data
        const colKeys = []
        line.forEach((text, j) => {
          const header = headers[firstCol + j]
          if (!header) return
          data = applyCellText(data, header.key, text)
          colKeys.push(header.key)
        })
        updates.set(target.key, { ...target, data })
        touched = markTouched(touched, target.key, colKeys)
      })
      const rows = [...state.rows, ...added].map((r) => updates.get(r.key) || r)
      return { ...state, rows, touched }
    }
    case "addRow":
      return { ...state, rows: [...state.rows, { key: action.rowKey, data: {}, isNew: true }] }
    case "deleteRow": {
      const touched = new Map(state.touched)
      touched.delete(action.rowKey)
      return { ...state, touched, rows: state.rows.filter((r) => r.key !== action.rowKey) }
    }
    case "clear":
      return { ...state, rows: [], touched: new Map() }
    case "reset":
      return { ...state, ...initGrid(action.sheet), sort: state.sort }
    case "sort":
      return { ...state, sort: nextSort(state.sort, action.colKey) }
    case "showErrors":
      return { ...state, showErrors: true }
    default:
      return state
  }
}

function ConversionSheetGrid({ sheet, sheetId, sheetType }) {
  const { t } = useT("messages")
  const { t: tc } = useT("common")
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [grid, update] = useReducer(gridReducer, sheet, initGrid)
  const [useBusinessDays, setUseBusinessDays] = useState(Boolean(sheet.useBusinessDays))
  const [confirmGoBack, setConfirmGoBack] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)
  const topRef = useRef(null)
  const tableRef = useRef(null)
  const focusRowKey = useRef(null)
  const goBackTimer = useRef(null)

  const headers = useMemo(() => orderHeaders(sheet.headers || [], sheetType), [sheet.headers, sheetType])
  const order = useMemo(() => displayOrder(grid.rows, grid.sort), [grid.rows, grid.sort])
  const isEdited =
    grid.rows !== grid.pristine || useBusinessDays !== Boolean(sheet.useBusinessDays)

  useEffect(() => () => clearTimeout(goBackTimer.current), [])

  // Focus the first cell of a row that was just added.
  useLayoutEffect(() => {
    const rowKey = focusRowKey.current
    if (!rowKey) return
    focusRowKey.current = null
    const cell = tableRef.current?.querySelector(`tr[data-rowkey="${rowKey}"] ${EDITABLE}`)
    cell?.focus()
    cell?.scrollIntoView({ block: "nearest" })
  }, [order])

  const onEdit = useCallback((rowKey, colKey, text) => update({ type: "edit", rowKey, colKey, text }), [])
  const onDelete = useCallback((rowKey) => update({ type: "deleteRow", rowKey }), [])
  const onPaste = useCallback(
    (rowKey, colKey, clipboardText) => {
      const cells = parseClipboard(clipboardText)
      const newKeys = cells.map(newRowKey)
      update({ type: "paste", rowKey, colKey, cells, headers, newKeys })
    },
    [headers]
  )

  const handleAddRow = () => {
    const rowKey = newRowKey()
    focusRowKey.current = rowKey
    update({ type: "addRow", rowKey })
  }

  const handleSave = () => {
    const hasInvalid = grid.rows.some((row) => headers.some((h) => !isCellValid(h.key, row.data[h.key])))
    if (hasInvalid) {
      update({ type: "showErrors" })
      dispatch(message.warning(t("adhoc.fillAllRequiredFields")))
      return
    }
    const save =
      sheetType === "Sample" ? integrationSheets.saveLimsConversionSheet : integrationSheets.saveSapConversionSheet
    dispatch(save(sheetId, grid.rows.map((row) => row.data), useBusinessDays)).then(() =>
      navigate(BACK_URL, BACK_STATE)
    )
  }

  const handleReset = () => {
    update({ type: "reset", sheet })
    setUseBusinessDays(Boolean(sheet.useBusinessDays))
  }

  const handleGoBack = () => {
    if (!isEdited || confirmGoBack) {
      navigate(BACK_URL, BACK_STATE)
      return
    }
    dispatch(message.warning(t("adhoc.unsavedChangesConfirmLeave")))
    setConfirmGoBack(true)
    clearTimeout(goBackTimer.current)
    goBackTimer.current = setTimeout(() => setConfirmGoBack(false), GO_BACK_CONFIRM_MS)
  }

  const handleExportExcel = () => {
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(exportRows(headers, order)), "Sheet1")
    XLSX.writeFile(workbook, tc("conversionSheetFilename"))
  }

  return (
    <div className="DataGridLayout-container">
      {confirmClear && (
        <ConfirmDialog
          onConfirm={() => {
            update({ type: "clear" })
            setConfirmClear(false)
          }}
          onCancel={() => setConfirmClear(false)}
        >
          <h4>{tc("confirmClearTable")}</h4>
        </ConfirmDialog>
      )}
      <h3 ref={topRef}>{tc("automaticImportConversionSheet", { type: sheetType })}</h3>
      <div className="data-grid-config-buttons">
        <div className="data-grid-left-button">
          <Button variant={isEdited && confirmGoBack ? "danger" : "primary"} title={tc("goBack")} onClick={handleGoBack}>
            <FiArrowLeft />
          </Button>
        </div>
        <div className="data-grid-center-buttons">
          <Button variant="secondary" onClick={handleAddRow}>
            {tc("addRow")}
          </Button>
          <Button variant="secondary" onClick={handleReset}>
            {tc("cancelChanges")}
          </Button>
          <Button onClick={handleSave}>{tc("save")}</Button>
          <Button variant="success" onClick={handleExportExcel}>
            {tc("exportToExcel")}
          </Button>
          <label className="business-days-label">
            <input type="checkbox" checked={useBusinessDays} onChange={(e) => setUseBusinessDays(e.target.checked)} />
            <span className="label-text">{tc("leadTimesInWorkDays")}</span>
          </label>
        </div>
        <div className="data-grid-right-button" style={{ visibility: grid.rows.length === 0 ? "hidden" : "" }}>
          <Button variant="danger" onClick={() => setConfirmClear(true)}>
            {tc("clearTable")}
          </Button>
        </div>
      </div>
      <div className="excelGrid-container">
        <table className="excelGrid" ref={tableRef}>
          <thead>
            <tr>
              {headers.map((header) => (
                <SortableHeader
                  key={header.key}
                  header={header}
                  sort={grid.sort}
                  title={tc("clickToSortBy", { column: header.name })}
                  onSort={() => update({ type: "sort", colKey: header.key })}
                />
              ))}
              <th className="delete-cell" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {order.map((row) => (
              <GridRow
                key={row.key}
                row={row}
                headers={headers}
                touchedCols={grid.touched.get(row.key)}
                showErrors={grid.showErrors}
                onEdit={onEdit}
                onPaste={onPaste}
                onDelete={onDelete}
                deleteTitle={tc("deleteThisRow")}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="add-row-btn-container">
        <Button variant="secondary" onClick={handleAddRow}>
          {tc("addRow")}
        </Button>
        <Button variant="primary" onClick={() => topRef.current?.scrollIntoView({ block: "start" })}>
          {tc("scrollToTop")}
        </Button>
      </div>
    </div>
  )
}

function SortableHeader({ header, sort, title, onSort }) {
  let Icon = FaSort
  if (sort?.key === header.key) Icon = sort.desc ? FaSortDown : FaSortUp
  return (
    <th className={header.bold ? "sortable bold" : "sortable"} title={title} onClick={onSort}>
      <div className="excelGrid-header-content">
        <span className="header-text">{header.name}</span>
        <span className="sort-icon">
          <Icon size={24} />
        </span>
      </div>
    </th>
  )
}

const GridRow = memo(function GridRow({ row, headers, touchedCols, showErrors, onEdit, onPaste, onDelete, deleteTitle }) {
  return (
    <tr data-rowkey={row.key}>
      {headers.map((header) => {
        const value = row.data[header.key]
        const invalid = (showErrors || touchedCols?.has(header.key)) && !isCellValid(header.key, value)
        return (
          <EditableCell
            key={header.key}
            rowKey={row.key}
            colKey={header.key}
            value={value}
            invalid={invalid}
            onEdit={onEdit}
            onPaste={onPaste}
          />
        )
      })}
      <td className="delete-cell">
        {/* Not reachable by Tab, so tabbing moves straight from cell to cell. */}
        <Button title={deleteTitle} variant="danger" onClick={() => onDelete(row.key)} tabIndex={-1}>
          <RiDeleteBinLine />
        </Button>
      </td>
    </tr>
  )
})

// The cell shows its value as plain text through textContent, so nothing is ever parsed as HTML.
function EditableCell({ rowKey, colKey, value, invalid, onEdit, onPaste }) {
  const ref = useRef(null)
  const text = cellText(value)

  useLayoutEffect(() => {
    if (ref.current.textContent !== text) ref.current.textContent = text
  }, [text])

  const handleBlur = (e) => {
    const typed = e.currentTarget.innerText
    // Show the normalised value even when it equals the stored one (e.g. "5 days" → "5").
    e.currentTarget.textContent = cellText(applyCellText({ [colKey]: value }, colKey, typed)[colKey])
    onEdit(rowKey, colKey, typed)
  }

  const handlePaste = (e) => {
    e.preventDefault()
    onPaste(rowKey, colKey, e.clipboardData.getData("text/plain"))
  }

  return (
    <td className={invalid ? "invalid-cell" : undefined}>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onBlur={handleBlur}
        onPaste={handlePaste}
        onKeyDown={handleGridKeyDown}
      />
    </td>
  )
}

// Keyboard navigation between cells. It follows the DOM, so it matches the on-screen order.
// Left/right move the caret inside the text and only jump to the next cell at its start or end.
function handleGridKeyDown(e) {
  if (e.nativeEvent.isComposing) return
  const cell = e.currentTarget
  const td = cell.closest("td")
  const tr = td.parentElement
  const col = Array.prototype.indexOf.call(tr.children, td)
  const cellIn = (row) => row?.children[col]?.querySelector(EDITABLE)

  let target
  let caretAtEnd = true
  switch (e.key) {
    case "Enter":
      target = cellIn(e.shiftKey ? tr.previousElementSibling : tr.nextElementSibling)
      e.preventDefault() // never insert a line break
      break
    case "ArrowDown":
      target = cellIn(tr.nextElementSibling)
      break
    case "ArrowUp":
      target = cellIn(tr.previousElementSibling)
      break
    case "ArrowLeft":
      if (caretOffset(cell) !== 0) return
      target = td.previousElementSibling?.querySelector(EDITABLE)
      break
    case "ArrowRight":
      if (caretOffset(cell) !== cell.textContent.length) return
      target = td.nextElementSibling?.querySelector(EDITABLE)
      caretAtEnd = false
      break
    default:
      return
  }
  if (!target) return
  e.preventDefault()
  target.focus()
  placeCaret(target, caretAtEnd)
}

// Caret position as a character offset into `el`, or -1 while a range is selected.
function caretOffset(el) {
  const selection = window.getSelection()
  if (!selection.rangeCount || !selection.isCollapsed) return -1
  const range = document.createRange()
  range.selectNodeContents(el)
  range.setEnd(selection.focusNode, selection.focusOffset)
  return range.toString().length
}

function placeCaret(el, atEnd) {
  const range = document.createRange()
  range.selectNodeContents(el)
  range.collapse(!atEnd)
  const selection = window.getSelection()
  selection.removeAllRanges()
  selection.addRange(range)
}
